<script lang="ts" setup>
import { LoaderCircle as LoadingIcon } from '@lucide/vue';
import { watchDebounced } from '@vueuse/core';
// noinspection ES6UnusedImports
import { motion } from 'motion-v';
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxPortal,
  ComboboxRoot,
  ComboboxViewport,
} from 'reka-ui';
import { computed, ref, toValue, useTemplateRef } from 'vue';
import type { AddressDetails, AddressSearch, AddressSearchResource } from '../address-auto-complete.types.js';
import { useAutocompleteSearch } from '../services/autocomplete.service.js';
import { ResetButton } from './index.js';

//-- definitions
defineProps<{
  placeholder: string;
  isFormSubmitted: boolean;
  disabled?: boolean;
  noResultsMessage: string;
  resetLabel: string;
  searchErrorMessage: string;
}>();

const emitter = defineEmits<{
  addressSelected: [addressDetails: AddressDetails | undefined];
}>();

const displayInput = useTemplateRef('displayInput');

//-- setup autocomplete results
const { suggestions, details, searchAddress, clear, getDetails, status } = useAutocompleteSearch();
const searchTerm = ref<string>('');

const isOpen = computed(() => {
  const statusVal = toValue(status);
  if (statusVal === 'loading' || statusVal === 'default') {
    return false;
  }

  if (statusVal === 'done') {
    return !details.value;
  }

  return statusVal === 'error';
});

watchDebounced(
  searchTerm,
  (term) => {
    if (term?.length > 3) {
      searchAddress(term);
    }

    if (term?.length === 0) {
      clear();
    }
  },
  { debounce: 300 },
);

watchDebounced(
  details,
  (_details) => {
    emitter('addressSelected', _details?.address);
  },
  { debounce: 300 },
);

//-- Utils
function getInputDisplayValue(term: AddressSearch | undefined) {
  if (!term) {
    return '';
  }

  return term.value;
}

function onSelect(item: AddressSearchResource) {
  if (item.links['continue-search']) {
    searchTerm.value = item.data.value;
  } else {
    getDetails(item).then(() => {
      displayInput.value?.focus?.();
    });
  }
}
</script>

<template>
  <ComboboxRoot v-model="details" :ignore-filter="true" :open="isOpen" :disabled="disabled">
    <ComboboxAnchor class="address-autocomplete-combobox__anchor">
      <ComboboxInput
        v-if="details === undefined"
        id="address-combobox"
        v-model="searchTerm"
        :aria-invalid="isFormSubmitted && !disabled"
        autocomplete="off"
        class="order-form__input-text address-autocomplete-combobox__input"
        :display-value="getInputDisplayValue"
        :placeholder="placeholder"
      />

      <input
        v-if="details !== undefined"
        ref="displayInput"
        :value="details.mail_lines.join(', ')"
        class="order-form__input-text address-autocomplete-combobox__input"
        readonly
      />
      <div class="address-autocomplete-combobox__icons">
        <ResetButton v-if="details !== undefined" @click="details = undefined">
          {{ resetLabel }}
        </ResetButton>
        <motion.span
          v-if="status === 'loading'"
          class="key-spinner"
          :animate="{ rotate: 360 }"
          :transition="{ duration: 1, ease: 'linear', repeat: Infinity }"
        >
          <LoadingIcon />
        </motion.span>
      </div>
    </ComboboxAnchor>
    <ComboboxPortal>
      <ComboboxContent
        :position="'popper'"
        :position-strategy="'absolute'"
        :collapse-padding="{ top: '20px', bottom: '20px' }"
        class="address-autocomplete-combobox-colors address-autocomplete-combobox__content"
      >
        <ComboboxViewport>
          <ComboboxEmpty class="address-autocomplete-combobox__option">
            <p v-if="status === 'done'">
              {{ noResultsMessage }}
            </p>
            <p v-if="status === 'error'">
              {{ searchErrorMessage }}
            </p>
          </ComboboxEmpty>
          <ComboboxItem
            v-for="suggestion in suggestions"
            :key="suggestion.data.value"
            :value="suggestion.data"
            class="address-autocomplete-combobox__option"
            @select.prevent="() => onSelect(suggestion)"
          >
            <div>
              <p>{{ suggestion.data.label }}</p>
              <small>{{ suggestion.data.description }}</small>
            </div>
          </ComboboxItem>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxPortal>
  </ComboboxRoot>
</template>

<!--suppress CssUnresolvedCustomProperty -->
<style>
.address-autocomplete-combobox__content {
  /*noinspection CssInvalidPropertyValue*/
  z-index: calc(1 * Infinity);
  width: var(--reka-popper-anchor-width);
  background: var(--background);
  color: var(--text);
  border-radius: var(--radius);
  border: 2px solid var(--border);
  margin-top: 4px;
  box-shadow: 0 2px 4px var(--shadow);
  padding-block: 0.5rem;
  max-height: var(--reka-combobox-content-available-height);
  max-width: var(--reka-combobox-content-available-width);
}

.address-autocomplete-combobox__input {
  width: 100cqi;
}

.address-autocomplete-combobox__option {
  margin-inline: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: var(--radius);

  transition: background-color 0.3s ease-in-out;
}

.address-autocomplete-combobox__option:hover,
.address-autocomplete-combobox__option[data-highlighted] {
  background-color: color-mix(var(--background) 95%, #000) !important;
}

.address-autocomplete-combobox__anchor {
  position: relative;
}

.address-autocomplete-combobox__icons {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
}

.key-spinner {
  display: block;
}
</style>
